import { __ } from '@wordpress/i18n';
import { useState } from 'react';
import { InspectorControls, AlignmentToolbar, BlockControls } from '@wordpress/block-editor';
import { TabPanel, PanelBody, PanelRow, RangeControl, ToggleControl, __experimentalUnitControl as UnitControl, Dashicon, ToolbarGroup, ToolbarButton, Button, SelectControl } from '@wordpress/components';
import produce from 'immer';

// Settings Components
import Title from '../../Components/Title';
import BColor from '../../Components/BColor';
import BtnGroup from '../../Components/BtnGroup';
import Background from '../../Components/Background';
import ColorsControl from '../../Components/ColorsControl';
import { InlineDetailMediaUpload } from '../../Components/MediaControl';
import { ChangeImageData } from '../../Components/ImageControl';
import BDevice from '../../Components/BDevice';
import BorderControl from '../../Components/BorderControl';
import MultiShadowControl from '../../Components/MultiShadowControl';
import Typography from '../../Components/Typography';
import SeparatorControl from '../../Components/SeparatorControl';
import SpaceControl from '../../Components/SpaceControl';
import { gearIcon } from '../../Components/Helper/icons';
import { pxUnit, perUnit, emUnit } from '../../Components/Helper/options';

import { themes, layouts, generalStyleTabs } from './utils/options';

const Settings = ({ attributes, setAttributes, updateMember, activeIndex, setActiveIndex }) => {
	const { members = [], columns, columnGap, rowGap, layout, theme, textAlign, padding, photoWidth, photoMargin, nameTypo, nameMargin, isTitle, titleTypo, titleMargin, isSep, sepMargin, isBio, bioTypo, bioMargin, isSocial, socialSize, socialIconMargin } = attributes;

	const [device, setDevice] = useState('desktop');

	const addMember = () => {
		const { background = { color: '#0000' }, border = { radius: '3px' }, shadow = {}, photoBorder = { radius: '50%' }, nameColor = '#333', titleColor = '#333', separator = { width: '20%', height: '3px', style: 'solid', color: '#777' }, bioColor = '#333', socialIconColors = { color: '#fff', bg: '#4527a4' } } = members[0] || {};

		setAttributes({
			members: [...members, {
				background,
				border,
				shadow,
				photo: { id: null, url: '', alt: '', title: '' },
				photoBorder,
				name: 'John Smith',
				nameColor,
				title: 'Manager',
				titleColor,
				separator,
				bio: 'I am a self-motivated and self-taught professional who likes to solve problems.',
				bioColor,
				social: [
					{ link: '#', icon: { class: 'fab fa-facebook-f', fontSize: 22, color: '#fff' } },
					{ link: '#', icon: { class: 'fab fa-twitter', fontSize: 22, color: '#fff' } },
					{ link: '#', icon: { class: 'fab fa-linkedin-in', fontSize: 22, color: '#fff' } }
				],
				socialIconColors
			}]
		});
		setActiveIndex(members.length);
	}

	const updateAllMembers = (params) => {
		const newMembers = produce(members, draft => {
			draft.map((__, index) => {
				for (const param of params) {
					const type = param[0];
					const value = param[1];

					draft[index][type] = value;
				}
			});
		});
		setAttributes({ members: newMembers });
	}

	const duplicateMember = e => {
		e.preventDefault();

		setAttributes({ members: [...members.slice(0, activeIndex), { ...members[activeIndex] }, ...members.slice(activeIndex)] });

		setActiveIndex(activeIndex + 1);
	}

	const removeMember = e => {
		e.preventDefault();

		setAttributes({ members: [...members.slice(0, activeIndex), ...members.slice(activeIndex + 1)] });

		setActiveIndex(0 === activeIndex ? 0 : activeIndex - 1);
	}

	const { background = {}, border = {}, shadow = {}, photo, photoBorder = {}, nameColor = '', titleColor = '', separator = {}, bioColor = '', socialIconColors = {} } = members[activeIndex] || {};

	return <>
		<InspectorControls>
			<TabPanel className='bPlTabPanel' activeClass='activeTab' tabs={generalStyleTabs}>{tab => <>
				{'general' === tab.name && <>
					<PanelBody className='bPlPanelBody help' title={__('Help', 'team-section')} initialOpen={false}>
						<div className='helpItem'>
							<a href='https://bblockswp.com/docs/team-block/' target='_blank' rel='noreferrer'><Dashicon icon='book' />{__('Read Documentation', 'team-section')}</a>
						</div>

						<div className='helpItem rateUs'>
							<a href='https://wordpress.org/support/plugin/team-section/reviews/#new-post' target='_blank' rel='noreferrer'>
								<span><Dashicon icon='star-filled' />{__('Would you please rate us?', 'team-section')}</span>
								<span>{__('We are new and we need your help to grow!🙏', 'team-section')}</span>
							</a>
						</div>
					</PanelBody>


					<PanelBody className='bPlPanelBody addRemoveItems editItem' title={__('Add or Remove Members', 'team-section')}>
						{null !== activeIndex && <>
							<h3 className='bplItemTitle'>{__(`Member ${activeIndex + 1}:`, 'team-section')}</h3>

							<Background label={__('Background:', 'team-section')} value={background} onChange={val => updateMember(activeIndex, 'background', val)} />

							<BorderControl label={__('Border:', 'team-section')} value={border} onChange={val => updateMember(activeIndex, 'border', val)} defaults={{ radius: '3px' }} />

							<MultiShadowControl label={__('Shadow:', 'team-section')} value={shadow} onChange={val => updateMember(activeIndex, 'shadow', val)} type='box' produce={produce} />

							<Title>{__('Photo:', 'team-section')}</Title>
							<InlineDetailMediaUpload value={photo} onChange={val => updateMember(activeIndex, 'photo', val)} placeholder={__('Enter Image URL', 'team-section')} />

							<ChangeImageData className='mt20' value={photo} onChange={val => updateMember(activeIndex, 'photo', val)} />

							<BorderControl label={__('Photo Border:', 'team-section')} value={photoBorder} onChange={val => updateMember(activeIndex, 'photoBorder', val)} defaults={{ radius: '50%' }} />

							<BColor label={__('Name Color:', 'team-section')} value={nameColor} onChange={val => updateMember(activeIndex, 'nameColor', val)} defaultColor='#333' />

							<BColor label={__('Designation/Title Color:', 'team-section')} value={titleColor} onChange={val => updateMember(activeIndex, 'titleColor', val)} defaultColor='#333' />

							<SeparatorControl value={separator} onChange={val => updateMember(activeIndex, 'separator', val)} defaults={{ width: '20%', height: '3px', color: '#777' }} />

							<BColor label={__('Bio Color:', 'team-section')} value={bioColor} onChange={val => updateMember(activeIndex, 'bioColor', val)} defaultColor='#333' />

							<ColorsControl label={__('Social Icon Colors:', 'team-section')} value={socialIconColors} onChange={val => updateMember(activeIndex, 'socialIconColors', val)} defaults={{ color: '#fff', bg: '#4527a4' }} />

							<PanelRow className='itemAction mt20 mb15'>
								{1 < members?.length && <Button className='removeItem' label={__('Remove', 'team-section')} onClick={removeMember}><Dashicon icon='no' />{__('Remove', 'team-section')}</Button>}

								<Button className='duplicateItem' label={__('Duplicate', 'team-section')} onClick={duplicateMember}>{gearIcon}{__('Duplicate', 'team-section')}</Button>
							</PanelRow>
						</>}

						<div className='addItem'>
							<Button label={__('Add New Member', 'team-section')} onClick={addMember}><Dashicon icon='plus' />{__('Add New Member', 'team-section')}</Button>
						</div>
					</PanelBody>


					<PanelBody className='bPlPanelBody' title={__('Layout Settings', 'team-section')} initialOpen={false}>
						<PanelRow>
							<Title className='mb5'>{__('Columns:', 'team-section')}</Title>
							<BDevice device={device} onChange={val => setDevice(val)} />
						</PanelRow>
						<RangeControl value={columns[device]} onChange={val => { setAttributes({ columns: { ...columns, [device]: val } }) }} min={1} max={6} step={1} beforeIcon='grid-view' />

						<UnitControl className='mt20' label={__('Column Gap:', 'team-section')} labelPosition='left' value={columnGap} onChange={val => setAttributes({ columnGap: val })} units={[pxUnit(), perUnit(), emUnit()]} />

						<UnitControl className='mt20' label={__('Row Gap:', 'team-section')} labelPosition='left' value={rowGap} onChange={val => setAttributes({ rowGap: val })} units={[pxUnit(), perUnit(), emUnit()]} />

						<PanelRow className='mt20'>
							<Title className=''>{__('Layout:', 'team-section')}</Title>
							<BtnGroup value={layout} onChange={val => {
								'vertical' === val && setAttributes({
									layout: val,
									columns: { ...columns, desktop: 3, tablet: 2 },
									textAlign: 'center',
									padding: { ...padding, vertical: '50px' },
								})
								'horizontal' === val && setAttributes({
									layout: val,
									columns: { ...columns, desktop: 2, tablet: 1 },
									textAlign: 'left',
									padding: { ...padding, vertical: '30px' },
								})
							}} options={layouts} isIcon={true} />
						</PanelRow>

						<PanelRow>
							<Title className=''>{__('Theme:', 'team-section')}</Title>
							<SelectControl value={theme} onChange={val => {
								setAttributes({ theme: val });
								'default' === val && setAttributes({
									padding: { ...padding, vertical: '50px', horizontal: '20px' },
									photoWidth: '170px',
									nameTypo: { ...nameTypo, fontSize: 20, textTransform: 'none' },
									titleTypo: { ...titleTypo, fontSize: 16, textTransform: 'none' },
									isSep: true,
									isBio: true,
									socialSize: '22px'
								});
								'theme1' === val && setAttributes({
									padding: { ...padding, vertical: '50px', horizontal: '20px' },
									photoWidth: '190px',
									nameTypo: { ...nameTypo, fontSize: 20, textTransform: 'none' },
									titleTypo: { ...titleTypo, fontSize: 14, textTransform: 'uppercase' },
									isSep: true,
									isBio: true,
									socialSize: '22px'
								});
								'theme2' === val && setAttributes({
									padding: { ...padding, vertical: '50px', horizontal: '20px' },
									photoWidth: '190px',
									nameTypo: { ...nameTypo, fontSize: 20, textTransform: 'none' },
									titleTypo: { ...titleTypo, fontSize: 14, textTransform: 'uppercase' },
									isSep: true,
									isBio: false,
									socialSize: '22px'
								});
								'theme3' === val && setAttributes({
									padding: { ...padding, vertical: '50px', horizontal: '20px' },
									photoWidth: '170px',
									nameTypo: { ...nameTypo, fontSize: 24, textTransform: 'none' },
									titleTypo: { ...titleTypo, fontSize: 18, textTransform: 'none' },
									isSep: false,
									isBio: false,
									socialSize: '28px'
								});
								'theme4' === val && setAttributes({
									padding: { ...padding, vertical: '15px', horizontal: '15px' },
									photoWidth: '100%',
									nameTypo: { ...nameTypo, fontSize: 20, textTransform: 'uppercase' },
									titleTypo: { ...titleTypo, fontSize: 15, textTransform: 'none' },
									isSep: false,
									isBio: false,
									socialSize: '22px'
								});

								members.map(member => {
									const { background, border, photoBorder, separator, socialIconColors } = member;

									const defaultParams = [
										['background', { ...background, color: '#0000' }],
										['border', { ...border, radius: '3px' }],
										['shadow', { shadow: [{ hOffset: '', vOffset: '', blur: '0px', color: '#7090b0' }] }],
										['photoBorder', { ...photoBorder, width: '0px', color: '#0000', radius: '50%' }],
										['nameColor', '#333'],
										['titleColor', '#333'],
										['separator', { ...separator, color: '#777' }],
										['bioColor', '#333'],
										['socialIconColors', { ...socialIconColors, color: '#fff', bg: '#4527a4' }]
									];
									const theme1Params = [
										['background', { ...background, color: '#0000' }],
										['border', { ...border, radius: '3px' }],
										['shadow', { shadow: [{ hOffset: '', vOffset: '', blur: '20px', color: '#e8edfb' }] }],
										['photoBorder', { ...photoBorder, width: '10px', color: '#e8edfb', radius: '50%' }],
										['nameColor', '#333'],
										['titleColor', '#7a7a7a'],
										['separator', { ...separator, color: '#4527a4' }],
										['bioColor', '#7a7a7a'],
										['socialIconColors', { ...socialIconColors, color: '#fff', bg: '#4527a4' }]
									];
									const theme2Params = [
										['background', { ...background, color: '#0000' }],
										['border', { ...border, radius: '3px' }],
										['shadow', { shadow: [{ hOffset: '', vOffset: '', blur: '0px', color: '#7090b0' }] }],
										['photoBorder', { ...photoBorder, width: '0px', color: '#0000', radius: '50%' }],
										['nameColor', '#333'],
										['titleColor', '#7a7a7a'],
										['separator', { ...separator, color: '#999' }],
										['bioColor', '#7a7a7a'],
										['socialIconColors', { ...socialIconColors, color: '#4527a4', bg: '#0000' }]
									];
									const theme3Params = [
										['background', { ...background, color: '#f2f3f7' }],
										['border', { ...border, radius: '10px' }],
										['shadow', { shadow: [{ hOffset: '-10px', vOffset: '-10px', blur: '20px', color: '#f7f7f7' }, { hOffset: '10px', vOffset: '10px', blur: '20px', color: '#d7e0e8' }] }],
										['photoBorder', { ...photoBorder, width: '0px', color: '#0000', radius: '50%' }],
										['nameColor', '#32285C'],
										['titleColor', '#677592'],
										['separator', { ...separator, color: '#777' }],
										['bioColor', '#333'],
										['socialIconColors', { ...socialIconColors, color: '#677592', bg: '#0000' }]
									];
									const theme4Params = [
										['background', { ...background, color: '#4527a4' }],
										['border', { ...border, radius: '0px' }],
										['shadow', { shadow: [{ hOffset: '', vOffset: '', blur: '0px', color: '#7090b0' }] }],
										['photoBorder', { ...photoBorder, width: '0px', color: '#0000', radius: '0px' }],
										['nameColor', '#fff'],
										['titleColor', '#fff'],
										['separator', { ...separator, color: '#777' }],
										['bioColor', '#fff'],
										['socialIconColors', { ...socialIconColors, color: '#4527a4', bg: '#fff' }]
									]

									'default' === val && updateAllMembers(defaultParams);
									'theme1' === val && updateAllMembers(theme1Params);
									'theme2' === val && updateAllMembers(theme2Params);
									'theme3' === val && updateAllMembers(theme3Params);
									'theme4' === val && updateAllMembers(theme4Params);
								});
							}} options={themes} />
						</PanelRow>
					</PanelBody>
				</>}


				{'style' === tab.name && <>
					<PanelBody className='bPlPanelBody' title={__('Member', 'team-section')} initialOpen={false}>
						<SpaceControl label={__('Padding:', 'team-section')} value={padding} onChange={val => setAttributes({ padding: val })} defaults={{ vertical: '50px', horizontal: '20px' }} />
					</PanelBody>


					<PanelBody className='bPlPanelBody' title={__('Member Photo', 'team-section')} initialOpen={false}>
						<UnitControl label={__('Width:', 'team-section')} labelPosition='left' value={photoWidth} onChange={val => setAttributes({ photoWidth: val })} units={[pxUnit(), perUnit(), emUnit()]} />

						<SpaceControl className='mt20' label={__('Margin:', 'team-section')} value={photoMargin} onChange={val => setAttributes({ photoMargin: val })} defaults={{ side: 4, bottom: '20px' }} />
					</PanelBody>


					<PanelBody className='bPlPanelBody' title={__('Member Name', 'team-section')} initialOpen={false}>
						<Typography value={nameTypo} onChange={val => setAttributes({ nameTypo: val })} defaults={{ fontSize: 20, fontWeight: 600 }} />

						<SpaceControl className='mt20' label={__('Margin:', 'team-section')} value={nameMargin} onChange={val => setAttributes({ nameMargin: val })} defaults={{ side: 4, bottom: '10px' }} />
					</PanelBody>


					<PanelBody className='bPlPanelBody' title={__('Designation/Title', 'team-section')} initialOpen={false}>
						<ToggleControl label={__('Show Title', 'team-section')} checked={isTitle} onChange={val => setAttributes({ isTitle: val })} />

						{isTitle && <>
							<Typography value={titleTypo} onChange={val => setAttributes({ titleTypo: val })} defaults={{ fontSize: 16 }} />

							<SpaceControl className='mt20' label={__('Margin:', 'team-section')} value={titleMargin} onChange={val => setAttributes({ titleMargin: val })} defaults={{ side: 4, bottom: '10px' }} />
						</>}
					</PanelBody>


					<PanelBody className='bPlPanelBody' title={__('Separator', 'team-section')} initialOpen={false}>
						<ToggleControl label={__('Show Separator', 'team-section')} checked={isSep} onChange={val => setAttributes({ isSep: val })} />

						{isSep && <SpaceControl className='mt20' label={__('Margin:', 'team-section')} value={sepMargin} onChange={val => setAttributes({ sepMargin: val })} defaults={{ side: 4, bottom: '15px' }} />}
					</PanelBody>


					<PanelBody className='bPlPanelBody' title={__('Member Bio', 'team-section')} initialOpen={false}>
						<ToggleControl label={__('Show Bio', 'team-section')} checked={isBio} onChange={val => setAttributes({ isBio: val })} />

						{isBio && <>
							<Typography value={bioTypo} onChange={val => setAttributes({ bioTypo: val })} defaults={{ fontSize: 15 }} />

							<SpaceControl className='mt20' label={__('Margin:', 'team-section')} value={bioMargin} onChange={val => setAttributes({ bioMargin: val })} defaults={{ side: 4, bottom: '15px' }} />
						</>}
					</PanelBody>


					<PanelBody className='bPlPanelBody' title={__('Member Social', 'team-section')} initialOpen={false}>
						<ToggleControl label={__('Show Social', 'team-section')} checked={isSocial} onChange={val => setAttributes({ isSocial: val })} />

						{isSocial && <>
							<UnitControl label={__('Icon Size:', 'team-section')} labelPosition='left' value={socialSize} onChange={val => setAttributes({ socialSize: val })} units={[pxUnit(), perUnit(), emUnit()]} />

							<SpaceControl className='mt20' label={__('Icon Margin:', 'team-section')} value={socialIconMargin} onChange={val => setAttributes({ socialIconMargin: val })} defaults={{ vertical: '10px', horizontal: '10px' }} />
						</>}
					</PanelBody>
				</>}
			</>}</TabPanel>
		</InspectorControls>


		<BlockControls>
			<ToolbarGroup className='bPlToolbar'>
				<ToolbarButton label={__('Add New Member', 'team-section')} onClick={addMember}><Dashicon icon='plus' /></ToolbarButton>
			</ToolbarGroup>

			<AlignmentToolbar value={textAlign} onChange={val => setAttributes({ textAlign: val })} />
		</BlockControls>
	</>;
};
export default Settings;